import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { catchError, map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { IItemOperationHierarchyNode, ItemOperationHierarchyService } from '../../shared/services/itemoperation-hierarchy.service';
import { TerminalAdministrationService } from '../../shared/services/terminalAdministration.service';
import { INodeColor, ITerminalStatus } from '../models/production-plan.models';

interface IPathSourceNode {
	id: string;
	nextId: string | null;
	terminalId: string | null;
	alias: string | null;
	sortKey: string;
	passthrough: boolean;
}

interface IPathNode {
	id: string;
	alias: string;
	terminalName: string;
	layer: number;
	x: number;
	y: number;
	passthrough: boolean;
	background?: string;
	accent?: string;
}

interface IPathTree {
	nodes: IPathNode[];
	edges: string[];
	width: number;
	height: number;
}

@Component({
	selector: 'app-production-plan-path',
	templateUrl: './production-plan-path.component.html',
	styleUrl: './production-plan-path.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ProductionPlanPathComponent implements OnInit, OnDestroy {
	@Input() terminalStatuses: ITerminalStatus[] = [];
	@Input() terminalNames?: Map<string, string>;
	@Input() nodeColorById?: Map<string, INodeColor>;
	@Input() isFullscreen = false;
	@Output() fullscreenToggle = new EventEmitter<void>();

	readonly nodeWidth = 220;
	readonly nodeHeight = 46;
	private readonly colWidth = 300;
	private readonly rowHeight = 58;

	paths: IPathTree[] = [];
	isLoading = true;
	loadError = false;
	showFull = false;

	private rawTrees: IItemOperationHierarchyNode[][] = [];
	private terminalNextId?: Map<string, string>;
	private destroy$ = new Subject<void>();

	constructor(
		private hierarchyService: ItemOperationHierarchyService,
		private terminalService: TerminalAdministrationService,
	) {}

	ngOnInit(): void {
		const ids = this.collectItemOperationIds();
		if (ids.length === 0) {
			this.isLoading = false;
			return;
		}

		this.resolvePaths(ids)
			.pipe(takeUntil(this.destroy$))
			.subscribe((trees) => {
				this.rawTrees = trees;
				this.rebuild();
				this.isLoading = false;
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	toggleFullscreen(): void {
		this.fullscreenToggle.emit();
	}

	onFullToggle(checked: boolean): void {
		this.showFull = checked;
		if (checked && !this.terminalNextId) {
			this.terminalService
				.getTerminals()
				.pipe(takeUntil(this.destroy$))
				.subscribe({
					next: (response) => {
						this.terminalNextId = new Map((response.result ?? []).filter((terminal) => terminal.id && terminal.nextId).map((terminal) => [terminal.id!, terminal.nextId!]));
						this.rebuild();
					},
					error: () => {
						this.showFull = false;
						this.rebuild();
					},
				});
			return;
		}
		this.rebuild();
	}

	private rebuild(): void {
		this.paths = this.rawTrees.map((tree) => this.layout(this.toSourceNodes(tree)));
	}

	private toSourceNodes(nodes: IItemOperationHierarchyNode[]): IPathSourceNode[] {
		if (this.showFull && this.terminalNextId) {
			return this.insertPassthroughs(nodes, this.terminalNextId);
		}
		return nodes.map((node) => this.toSource(node));
	}

	private toSource(node: IItemOperationHierarchyNode): IPathSourceNode {
		return {
			id: node.id,
			nextId: node.nextId,
			terminalId: node.terminalId,
			alias: node.alias,
			sortKey: node.alias,
			passthrough: false,
		};
	}

	private insertPassthroughs(nodes: IItemOperationHierarchyNode[], chain: Map<string, string>): IPathSourceNode[] {
		const sources = new Map<string, IPathSourceNode>(nodes.map((node) => [node.id, this.toSource(node)]));
		const passthroughs = new Map<string, IPathSourceNode>();
		let counter = 0;

		for (const source of sources.values()) {
			const parent = source.nextId ? sources.get(source.nextId) : undefined;
			if (!parent?.terminalId || !source.terminalId || parent.terminalId === source.terminalId) {
				continue;
			}

			const skipped: string[] = [];
			let terminal = chain.get(source.terminalId) ?? null;
			let guard = 0;
			while (terminal && terminal !== parent.terminalId && guard++ < chain.size) {
				skipped.push(terminal);
				terminal = chain.get(terminal) ?? null;
			}
			if (terminal !== parent.terminalId || skipped.length === 0) {
				continue;
			}

			let target = parent.id;
			for (const terminalId of skipped.reverse()) {
				const key = `${terminalId}|${target}`;
				const passthrough = passthroughs.get(key) ?? {
					id: `__passthrough_${counter++}`,
					nextId: target,
					terminalId,
					alias: null,
					sortKey: source.sortKey,
					passthrough: true,
				};
				passthroughs.set(key, passthrough);
				target = passthrough.id;
			}
			source.nextId = target;
		}

		const reverseChain = new Map<string, string[]>();
		for (const [id, next] of chain) {
			const predecessors = reverseChain.get(next);
			if (predecessors) {
				predecessors.push(id);
			} else {
				reverseChain.set(next, [id]);
			}
		}

		const targeted = new Set([...sources.values(), ...passthroughs.values()].map((n) => n.nextId).filter((id) => !!id));
		const leaves = [...sources.values(), ...passthroughs.values()].filter((node) => node.terminalId && !targeted.has(node.id));

		const extendBackward = (node: IPathSourceNode, guard: number): void => {
			if (guard <= 0 || !node.terminalId) {
				return;
			}
			for (const terminalId of reverseChain.get(node.terminalId) ?? []) {
				const key = `${terminalId}|${node.id}`;
				if (passthroughs.has(key)) {
					continue;
				}
				const passthrough: IPathSourceNode = {
					id: `__passthrough_${counter++}`,
					nextId: node.id,
					terminalId,
					alias: null,
					sortKey: node.sortKey,
					passthrough: true,
				};
				passthroughs.set(key, passthrough);
				extendBackward(passthrough, guard - 1);
			}
		};
		leaves.forEach((leaf) => extendBackward(leaf, chain.size));

		return [...sources.values(), ...passthroughs.values()];
	}

	private collectItemOperationIds(): string[] {
		const ids = this.terminalStatuses.flatMap((status) => status.itemOperations.map((operation) => operation.id).filter((id): id is string => !!id));
		return [...new Set(ids)];
	}

	private resolvePaths(pending: string[]): Observable<IItemOperationHierarchyNode[][]> {
		if (pending.length === 0) {
			return of([]);
		}

		const [head, ...rest] = pending;
		return this.hierarchyService.getItemOperationHierarchy(head).pipe(
			catchError(() => {
				this.loadError = true;
				return of<IItemOperationHierarchyNode[]>([]);
			}),
			switchMap((nodes) => {
				const treeIds = new Set(nodes.map((node) => node.id));
				const remaining = rest.filter((id) => !treeIds.has(id));
				return this.resolvePaths(remaining).pipe(map((trees) => (nodes.length > 0 ? [nodes, ...trees] : trees)));
			}),
		);
	}

	private layout(nodes: IPathSourceNode[]): IPathTree {
		const byId = new Map(nodes.map((node) => [node.id, node]));
		const parentId = (node: IPathSourceNode): string | null => (node.nextId && byId.has(node.nextId) ? node.nextId : null);

		const children = new Map<string, IPathSourceNode[]>();
		const ends: IPathSourceNode[] = [];
		for (const node of nodes) {
			const parent = parentId(node);
			if (!parent) {
				ends.push(node);
				continue;
			}
			const siblings = children.get(parent);
			if (siblings) {
				siblings.push(node);
			} else {
				children.set(parent, [node]);
			}
		}

		ends.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
		for (const siblings of children.values()) {
			siblings.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
		}

		const col = new Map<string, number>();
		const row = new Map<string, number>();
		let leafRow = 0;
		const place = (node: IPathSourceNode, column: number): void => {
			if (col.has(node.id)) {
				return;
			}
			col.set(node.id, column);
			const kids = children.get(node.id) ?? [];
			if (kids.length === 0) {
				row.set(node.id, leafRow++);
				return;
			}
			kids.forEach((kid) => place(kid, column + 1));
			row.set(node.id, (row.get(kids[0].id)! + row.get(kids[kids.length - 1].id)!) / 2);
		};
		ends.forEach((end) => place(end, 0));

		const maxCol = Math.max(0, ...col.values());
		for (const node of nodes) {
			if (!children.has(node.id)) {
				col.set(node.id, maxCol);
			}
		}
		const rowCount = Math.max(1, leafRow);
		const x = (id: string): number => (maxCol - (col.get(id) ?? 0)) * this.colWidth;
		const y = (id: string): number => (row.get(id) ?? 0) * this.rowHeight;

		const placed: IPathNode[] = nodes.map((node) => {
			const alias = (node.alias ?? '').trim();
			const color = node.passthrough ? undefined : this.nodeColorById?.get(node.id);
			return {
				id: node.id,
				alias: alias || '—',
				terminalName: this.terminalName(node.terminalId),
				layer: (alias.match(/:/g) ?? []).length,
				x: x(node.id),
				y: y(node.id),
				passthrough: node.passthrough,
				background: color?.background,
				accent: color?.accent,
			};
		});

		const gutter = this.colWidth - this.nodeWidth;

		const edges: string[] = [];
		for (const node of nodes) {
			const parent = parentId(node);
			if (!parent) {
				continue;
			}
			const x1 = x(node.id) + this.nodeWidth;
			const y1 = y(node.id) + this.nodeHeight / 2;
			const x2 = x(parent);
			const y2 = y(parent) + this.nodeHeight / 2;

			const columnSpan = (col.get(node.id) ?? 0) - (col.get(parent) ?? 0);
			if (columnSpan > 1) {
				const approachX = x2 - gutter;
				const mx = (approachX + x2) / 2;
				edges.push(`M${x1},${y1} L${approachX},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`);
				continue;
			}

			const mx = (x1 + x2) / 2;
			edges.push(`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`);
		}

		return {
			nodes: placed,
			edges,
			width: maxCol * this.colWidth + this.nodeWidth,
			height: (rowCount - 1) * this.rowHeight + this.nodeHeight,
		};
	}

	private terminalName(terminalId: string | null): string {
		if (!terminalId) {
			return '';
		}
		return this.terminalNames?.get(terminalId) ?? terminalId;
	}
}
